import { useState, useMemo, useEffect, useContext, useRef } from "react";
import { v4 as uuid } from "uuid";
import firebase from "../../firebase/compat";
import { db, timestamp } from "../../firebase/config";
import { client } from "../../hooks/Client";
import { Auth } from "../../StateManagment/Auth";
import { SavedIcon } from "../../StateManagment/SavedIcon";
import { DeccaContext } from "../../StateManagment/DeccaContext";
import { useFirestoreOrderBy } from "../../firebase/useFirestore";
import ScrollToTop from "../../hooks/ScrollToTop";
import PageHeader from "../../components/layout/PageHeader.jsx";
import DeleteModal from "../../components/common/DeleteModal.jsx";
import { createPortal } from "react-dom";
import OpenInFullOutlinedIcon from "@mui/icons-material/OpenInFullOutlined";
import CloseOutlinedIcon from "@mui/icons-material/CloseOutlined";
import NodeEditor from "../../components/impactStrategy/NodeEditor.jsx";
import usePermissions from "../../hooks/usePermissions";
import NodeDetail from "../../components/impactStrategy/NodeDetail.jsx";
import TocChainOverview from "../../components/impactStrategy/TocChainOverview.jsx";
import AddTargetgroupFlow from "../../components/impactStrategy/add/AddTargetgroupFlow.jsx";
import AddActivityFlow from "../../components/impactStrategy/add/AddActivityFlow.jsx";
import AddEffectFlow from "../../components/impactStrategy/add/AddEffectFlow.jsx";
import AddMechanismsModal from "../../components/mechanisms/AddMechanismsModal.jsx";
import resolveAddMode from "../../helpers/impactStrategy/resolveAddMode";
import buildMechanismDocs from "../../helpers/impactStrategy/buildMechanismDocs";
import chainLinkUpdates from "../../helpers/tocHelpers/chainLinkUpdates";
import resolveLinkTarget from "../../helpers/tocHelpers/resolveLinkTarget";
import reorderStepPositions from "../../helpers/tocHelpers/reorderStepPositions";
import useParentPortfolios from "../../hooks/useParentPortfolios";
import useSyncedLinks from "../../hooks/useSyncedLinks";
import LinkedBadge from "../../components/common/LinkedBadge.jsx";
import buildSectorContextUpdate from "../../helpers/impactStrategy/buildSectorContextUpdate";
import SectorContextBand from "../../components/impactStrategy/SectorContextBand.jsx";
import { sectorContextGroupsFor } from "../../helpers/dataHelpers/sectorContextQuestions";
import tocDeccaGreeting from "../../helpers/impactStrategy/tocDeccaGreeting";
import useSettings from "../../hooks/Settings";
import {
  STEP_ORDER,
  buildNavigatorModel,
  optionsAt,
  getNode,
  placeAtLevel,
} from "../../helpers/tocHelpers/tocNavigatorChain";
import { mechanismCategoryLabels } from "../../helpers/dataHelpers/mechanismCategories";

/**
 * Per-step display metadata: label, sub, accent colour, SVG icon, and the
 * route of the dedicated edit page for that step (the "Bewerken" button
 * navigates there).
 * @typedef {object} StepMeta
 * @property {string} label
 * @property {string} short
 * @property {string} sub
 * @property {string} color
 * @property {string} icon - Inner SVG markup (24x24 viewBox).
 * @property {string} route - URL segment of the dedicated edit page.
 */

/** @type {Record<string, StepMeta>} */
const STEP_META = {
  doel: {
    label: "Doelgroep", short: "Doelgroep", sub: "Taxonomie", color: "#c79a3e",
    icon: '<circle cx="12" cy="8" r="3.2" stroke="currentColor" stroke-width="1.8"/><path d="M5 20a7 7 0 0 1 14 0" stroke="currentColor" stroke-width="1.8" stroke-linecap="round"/>',
    route: "Stakeholders",
  },
  act: {
    label: "Activiteit", short: "Activiteit", sub: "Wat doen we", color: "#3f93a8",
    icon: '<path d="M5 12l4 4L19 6" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>',
    route: "Activities",
  },
  mech: {
    label: "Werkzaam mechanisme", short: "Mechanisme", sub: "Waarom werkt het", color: "#8a5fb0",
    icon: '<path d="M12 3v3M12 18v3M5 12H2M22 12h-3M6 6l2 2M16 16l2 2M18 6l-2 2M8 16l-2 2" stroke="currentColor" stroke-width="1.7" stroke-linecap="round"/><circle cx="12" cy="12" r="3.4" stroke="currentColor" stroke-width="1.7"/>',
    route: "Mechanisms",
  },
  eff: {
    label: "Effect", short: "Effect", sub: "Wat verandert", color: "#4aa06a",
    icon: '<path d="M4 18l5-5 3 3 7-8" stroke="currentColor" stroke-width="1.9" stroke-linecap="round" stroke-linejoin="round"/><path d="M16 8h4v4" stroke="currentColor" stroke-width="1.9" stroke-linecap="round" stroke-linejoin="round"/>',
    route: "effects",
  },
};

/**
 * The board ("Overzicht") is NOT a chain step (never added to STEP_ORDER — that
 * array drives optionsAt, Decca step reporting and the backend assembler). It
 * only carries the accent colour for the board stage.
 */
const OVERZICHT_META = {
  color: "#206c7f",
};

/**
 * Sector-specific context questions. Like the board, NOT a chain step — it never
 * enters STEP_ORDER, which drives optionsAt, Decca's step reporting and the
 * backend assembler. It lives in the breadcrumb's reserved first track (44px),
 * so the four step items keep aligning with the four board columns.
 * See docs/features/sector-context-questions.md
 */
const CONTEXT_META = {
  label: "Context",
  sub: "Kenmerken van je organisatie",
  color: "#206c7f",
  icon: '<path d="M12 8h.01M11 12h1v4h1" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"/><circle cx="12" cy="12" r="8.4" stroke="currentColor" stroke-width="1.7"/>',
};

/** Firestore collection backing each editable chain step (for inline create). */
const STEP_COLLECTION = {
  doel: "Stakeholders",
  act: "Activities",
  mech: "TocMechanisms",
  eff: "OutputEffects",
};

const chevron =
  '<path d="M9 6l6 6-6 6" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>';

const plusIcon =
  '<path d="M12 5v14M5 12h14" stroke="currentColor" stroke-width="2" stroke-linecap="round"/>';

/** Small helper to render inner SVG markup. */
const Svg = ({ markup, className }) => (
  <svg viewBox="0 0 24 24" fill="none" className={className} dangerouslySetInnerHTML={{ __html: markup }} />
);

/**
 * Theory of Change navigator. Walks the five-step chain one step at a time with
 * a breadcrumb stepper: the org builds doelgroep → activiteit → mechanisme →
 * effect (editable in-place). The external framing — maatschappelijk probleem +
 * impactdoelen — is derived and read-only, rendered as bands that bookend the
 * chain (it surrounds the TOC, it's not a step).
 */
const Impactstrategy = () => {
  ScrollToTop();

  // Firestore — the four editable chain collections (mechanisms minus
  // soft-deleted). The probleem + impactdoelen context is derived (no collection).
  const stakeholders = useFirestoreOrderBy("Stakeholders", "Position", "asc");
  const activities = useFirestoreOrderBy("Activities", "Position", "asc");
  const allMechanisms = useFirestoreOrderBy("TocMechanisms", "Position", "asc");
  const effects = useFirestoreOrderBy("OutputEffects", "Position", "asc");
  const allOutputs = useFirestoreOrderBy("Outputs", "Position", "asc");
  // Sector-specific context answers live on CompagnyMeta alongside the sector
  // choice itself. See docs/features/sector-context-questions.md
  const { selectedSectors: sectors, sectorContext, docid: compagnyDocid, personasOn, housevisits } = useSettings();
  // Module flags gate the output measurement methods in the editor (ADR-011).
  const outputModules = useMemo(() => ({ personasOn, housevisits }), [personasOn, housevisits]);
  // No sector, or a sector without questions → no pill, no stage, nothing.
  const hasSectorContext = sectorContextGroupsFor(sectors).length > 0;

  // Soft-deleted docs (Deleted: true, ADR-008) stay in Firestore but must drop
  // out of the navigator entirely.
  const liveStakeholders = useMemo(() => (stakeholders || []).filter((s) => !s.Deleted), [stakeholders]);
  const liveActivities = useMemo(() => (activities || []).filter((a) => !a.Deleted), [activities]);
  const mechanisms = useMemo(() => (allMechanisms || []).filter((m) => !m.Deleted), [allMechanisms]);
  const liveEffects = useMemo(() => (effects || []).filter((e) => !e.Deleted), [effects]);
  const outputs = useMemo(() => (allOutputs || []).filter((o) => !o.Deleted), [allOutputs]);

  const model = useMemo(
    () => buildNavigatorModel({ stakeholders: liveStakeholders, activities: liveActivities, mechanisms, effects: liveEffects }),
    [liveStakeholders, liveActivities, mechanisms, liveEffects]
  );

  // When the WHOLE model is empty the Overzicht board is replaced by a single
  // "Doelgroep toevoegen" pill under the Doelgroep step (header stays). A
  // tenant with e.g. activities but no doelgroepen still gets the board (its
  // empty columns are clickable add-affordances).
  const modelEmpty = STEP_ORDER.every((step) => model.byStep[step].length === 0);

  // path = chosen node id per level, contiguous from level 0
  const [path, setPath] = useState(/** @type {string[]} */ ([]));
  // 'chain' = the board (every column, all items); 'detail' = one node's
  // detail/editor. The board is the single overview — there is no per-column
  // list mode.
  const [mode, setMode] = useState("chain"); // 'chain' | 'detail' | 'context'
  // 'Op groot scherm' (inbox #329): the whole navigator card fills the browser
  // window, for a TOC with more effects than fit the page. It is a mode of the
  // card, not a modal: board, detail and context all stay full-screen until the
  // user clicks the X.
  const [fullscreen, setFullscreen] = useState(false);
  // Whether the next add should wire an upstream chain link. Board adds are
  // UNLINKED (the user drags to connect); detail-mode sibling adds link into the
  // current chain. A ref, so the async chooser callback reads the right intent.
  const addLinkRef = useRef(true);
  // The node id currently being edited inline in its active card (F7). Editing
  // is scoped to the one item the user picked, not the whole step.
  const [editingNodeId, setEditingNodeId] = useState(/** @type {string | null} */ (null));
  // Soft-delete confirmation modal for the active node.
  const [deleteOpen, setDeleteOpen] = useState(false);
  // Which step's add-chooser modal is open (null = closed). Mech and — without
  // parent portfolios — doel/act skip the chooser (resolveAddMode → 'direct').
  const [chooserStep, setChooserStep] = useState(/** @type {'doel'|'act'|'mech'|'eff'|null} */ (null));
  // Latch per step: a flow mounts on first open and then STAYS mounted (its
  // follow-up subscribe modal must survive the chooser closing), but until that
  // first open it costs nothing — no portfolio listeners, no Meetstandaard fetch.
  const [mountedFlows, setMountedFlows] = useState(/** @type {Record<string, boolean>} */ ({}));
  const { parentPortfolioIds, portfolioNameById } = useParentPortfolios();

  // Portfolio-paired elements get a link badge: local element ID → parent
  // portfolio (from the tenant's Synchronisations rows).
  const syncedLinks = useSyncedLinks();
  const linkedBadgeFor = (id) =>
    id && syncedLinks[id]
      ? <LinkedBadge portfolioName={portfolioNameById[syncedLinks[id]]} />
      : null;
  const [auth] = useContext(Auth);
  const { setTocContext } = useContext(DeccaContext);

  // Edit-gating (role permissions): view-only users navigate the chain but
  // can't create, edit or delete TOC elements. Permissive while loading.
  const [, setSaved] = useContext(SavedIcon);
  const { has: hasPermission, ready: permissionsReady } = usePermissions();
  const canEdit = !permissionsReady || hasPermission("strategy.edit");

  // Seed the path with the first doelgroep once data has loaded (doelgroep is
  // now the chain root).
  const firstStakeholderId = liveStakeholders[0]?.ID;
  useEffect(() => {
    if (path.length === 0 && firstStakeholderId) setPath([firstStakeholderId]);
  }, [firstStakeholderId, path.length]);

  const level = path.length - 1;

  // ── Decca context awareness ──
  // Tell Decca where the user is in the navigator so the assistant can speak to
  // the current step/node and what's still open there. On the board no single
  // node is selected, so the focus is step-level. This is transient
  // context: DeccaContext auto-clears it when the sidebar closes, and the
  // unmount effect below clears it when the user leaves the page — so TOC
  // position never leaks into a chat opened elsewhere.
  // On the board no step is active: report the pseudo-step 'overzicht' with mode
  // 'overview' — the backend assembler falls back gracefully for unknown steps
  // (raw label, empty list).
  // Neither the board nor the context questions is a chain step, so both report
  // a pseudo-step rather than an index into STEP_ORDER.
  const tocStep =
    mode === "chain" ? "overzicht" : mode === "context" ? "sectorcontext" : STEP_ORDER[level];
  const tocNodeId = mode === "detail" ? (path[level] || null) : null;
  const tocMode = mode === "chain" ? "overview" : mode;
  useEffect(() => {
    setTocContext({ step: tocStep, nodeId: tocNodeId, path, mode: tocMode });
  }, [tocStep, tocNodeId, tocMode, path, setTocContext]);
  useEffect(() => () => setTocContext(null), [setTocContext]);

  // Context-aware opening message for the "Vraag Decca om advies" button, so the
  // first Decca message reflects where the user is (cold start vs. a specific
  // step/node, detail vs. overview) instead of a generic greeting.
  const deccaGreeting = useMemo(
    () => tocDeccaGreeting({
      userName: auth?.ForName,
      stepLabel: STEP_META[tocStep]?.label,
      nodeTitle: getNode(model, tocNodeId)?.title,
      mode: tocMode,
      isEmpty: liveStakeholders.length === 0,
    }),
    [auth, tocStep, tocNodeId, model, tocMode, liveStakeholders.length]
  );

  // ── navigation ──
  const pick = (id, pickLevel) => {
    setPath(path.slice(0, pickLevel).concat(id));
    setMode("detail");
    setEditingNodeId(null);
  };
  // Return to the board from any node's detail (breadcrumb column heading).
  const showBoard = () => {
    setMode("chain");
    setEditingNodeId(null);
  };

  // Jump from the board to a node's detail. The board shows every node
  // regardless of the current path, so upstream levels are backfilled with a
  // LINKED ancestor where one exists (first linked; else the step's first node)
  // to keep the chain coherent.
  const pickFromOverview = (id, pickLevel) => {
    const p = new Array(pickLevel);
    let childId = id;
    for (let i = pickLevel - 1; i >= 0; i--) {
      const child = getNode(model, childId);
      let parentId = null;
      if (child?.step === "act") {
        parentId = (child.raw.Targetgroup || []).find((tid) => getNode(model, tid)) || null;
      } else if (child?.step === "mech") {
        parentId = (child.raw.Activity || []).find((aid) => getNode(model, aid)) || null;
      } else if (child?.step === "eff") {
        parentId = model.byStep.mech.find((m) => (m.raw.Effect || []).includes(childId))?.id || null;
      }
      if (!parentId) parentId = model.byStep[STEP_ORDER[i]][0]?.id;
      // An empty intermediate step leaves no candidate at all — store '' (not
      // undefined) so path serialises cleanly (Decca context) and downstream
      // falsy-checks (renderBreadcrumb ghosts, optionsAt) behave the same.
      p[i] = parentId || "";
      childId = parentId;
    }
    setPath(p.concat(id));
    setMode("detail");
    setEditingNodeId(null);
  };

  // Create a blank element for a step, then jump to it in edit mode. When
  // linkUpstream is true (detail-mode sibling add) it also wires the upstream
  // chain link so it shows up in the current chain; board adds pass false and
  // create an UNLINKED node (the user drags to connect it).
  const createNode = async (step, linkUpstream = true) => {
    const id = uuid();
    const ref = db.collection(STEP_COLLECTION[step]).doc();
    const position = (model.byStep[step]?.length || 0) + 1;
    const base = {
      docid: ref.id,
      ID: id,
      Description: "",
      Position: position,
      Compagny: client,
      CompagnyID: client,
      Timestamp: timestamp,
    };

    let doc;
    if (step === "doel") {
      doc = { ...base, Organisation: "" };
    } else if (step === "act") {
      doc = { ...base, Activity: "", Targetgroup: linkUpstream && path[0] ? [path[0]] : [] };
    } else if (step === "mech") {
      doc = { ...base, Title: "", Category: "", Activity: linkUpstream && path[1] ? [path[1]] : [], Effect: [] };
    } else if (step === "eff") {
      doc = { ...base, Effect: "" };
    } else {
      return;
    }

    // Place the new node at its own step index (padding missing upstream slots)
    // so it can't collapse into an earlier step's column — same guard as
    // handleChooserAdded.
    const newPath = placeAtLevel(path, STEP_ORDER.indexOf(step), id);

    await ref.set(doc);

    // An effect is linked FROM its mechanism (mech.Effect[] holds effect IDs), so
    // push the new effect onto the current mechanism to make it part of the chain
    // — only for a linked (detail-mode) add.
    if (linkUpstream && step === "eff") {
      const mech = getNode(model, path[2]);
      if (mech?.raw?.docid) {
        const next = Array.isArray(mech.raw.Effect) ? [...mech.raw.Effect, id] : [id];
        await db.collection("TocMechanisms").doc(mech.raw.docid).update({ Effect: next });
      }
    }

    setPath(newPath);
    setMode("detail");
    setEditingNodeId(id);
  };

  // Batch-create every mechanism the user ticked in the picker. Mirrors
  // createNode('mech') per document — same upstream link to the current
  // activiteit, same Position sequence — but as one batch, so a multi-pick
  // can't half-land. A board add (addLinkRef false) stays unlinked, exactly
  // like createNode's linkUpstream=false path.
  const createMechanismNodes = async (choices) => {
    const items = (choices || []).filter(Boolean);
    if (items.length === 0) return;

    const payloads = buildMechanismDocs({
      choices: items,
      activityId: addLinkRef.current ? path[1] : null,
      startPosition: (model.byStep.mech?.length || 0) + 1,
      client,
      timestamp,
    });

    const batch = db.batch();
    const created = payloads.map((payload) => {
      const id = uuid();
      const ref = db.collection(STEP_COLLECTION.mech).doc();
      batch.set(ref, { ...payload, docid: ref.id, ID: id });
      return { id, docid: ref.id };
    });
    await batch.commit();

    // Same placement guard as handleChooserAdded: put the first new node at the
    // mechanism level so the breadcrumb frames it correctly.
    setPath(placeAtLevel(path, STEP_ORDER.indexOf("mech"), created[0].id));
    setMode("detail");
    setEditingNodeId(null);
  };

  // "+ toevoegen" dispatch: open the source chooser (eigen / Meetstandaard /
  // portfolio) where one exists for the step, otherwise create directly.
  // linkUpstream carries the board-vs-chain intent through to createNode /
  // handleChooserAdded (board adds are unlinked).
  // AI-CONTEXT: See docs/features/toc-navigator-add-chooser.md
  const openAdd = (step, linkUpstream = true) => {
    if (resolveAddMode(step, parentPortfolioIds) === "chooser") {
      addLinkRef.current = linkUpstream;
      setMountedFlows((prev) => (prev[step] ? prev : { ...prev, [step]: true }));
      setChooserStep(step);
    } else {
      createNode(step, linkUpstream);
    }
  };

  // After a Meetstandaard save or portfolio subscribe: link the new items into
  // the current chain (activiteit → doelgroep, effect → mechanisme), then jump
  // to the first one in detail mode. Items without an anchor stay unlinked and
  // remain reachable via the Overzicht board.
  const handleChooserAdded = (step) => async (added) => {
    const items = (added || []).filter(Boolean);
    if (items.length === 0) return;

    // Board adds (addLinkRef false) stay unlinked — the user drags to connect.
    if (addLinkRef.current) {
      const mechDocid = getNode(model, path[2])?.raw?.docid;
      const updates = chainLinkUpdates(step, { doelId: path[0], mechDocid }, items);
      await Promise.all(
        updates.map((update) =>
          db.collection(update.collection).doc(update.docid).update({
            [update.field]: firebase.firestore.FieldValue.arrayUnion(...update.values),
          })
        )
      );
    }

    // Place the new node at its OWN step index so the breadcrumb column and
    // detail banner (which read the label from the path position) frame it
    // correctly. A bare `path.slice(0, 3).concat` dropped an effect into the
    // mechanism slot whenever no mechanism was selected. placeAtLevel pads the
    // upstream chain instead of shifting the new node up a level.
    const firstId = items[0].id;
    setPath(placeAtLevel(path, STEP_ORDER.indexOf(step), firstId));
    setMode("detail");
    setEditingNodeId(null);
  };

  // Soft-delete (ADR-008) the active node, then drop it from the chain and
  // return to the board so the user can pick or add another.
  const softDeleteNode = (node) => {
    if (!node?.raw?.docid) return;
    db.collection(STEP_COLLECTION[node.step]).doc(node.raw.docid).update({
      Deleted: true,
      DeletedAt: timestamp,
      DeletedBy: auth?.ID || "",
    });
    setEditingNodeId(null);
    setPath(path.slice(0, level));
    setMode("chain");
  };

  // ── breadcrumb — the single header across all modes. On the Overzicht board
  // its step items double as the column headers (the board's 5-track grid
  // aligns each column under its step; see tocNavigator.css). ──
  const renderBreadcrumb = () => {
    const curLevel = mode === "chain" || mode === "context" ? -1 : level;
    return (
      <div className="tocnav-bc">
        {/* Track 1 is the 44px gutter the four steps are offset by. Only the
            context pill ever occupies it; without questions it stays empty and
            the header looks exactly as before. Icon-only on purpose — widening
            this track would push the steps out of line with the board columns. */}
        {hasSectorContext && (
          <button
            type="button"
            className={`tocnav-bc-context${mode === "context" ? " is-current" : ""}`}
            style={{ "--sc": CONTEXT_META.color }}
            aria-label={CONTEXT_META.label}
            aria-pressed={mode === "context"}
            title={`${CONTEXT_META.label} — ${CONTEXT_META.sub}`}
            onClick={() => setMode("context")}
          >
            <Svg markup={CONTEXT_META.icon} />
          </button>
        )}
        {STEP_ORDER.map((step, i) => {
          const meta = STEP_META[step];
          const resolved = i < path.length && !!path[i];
          const node = resolved ? getNode(model, path[i]) : null;
          return (
            <div className="tocnav-bc-row" key={step}>
              {/* Separators only between the steps. */}
              {i > 0 && <span className="tocnav-bc-sep"><Svg markup={chevron} /></span>}
              {/* A column heading returns to the board (the single overview). */}
              <div
                className={`tocnav-bc-step${i === curLevel ? " is-current" : ""}${resolved ? "" : " is-ghost"}`}
                style={{ "--sc": meta.color }}
                onClick={showBoard}
              >
                <span className="tocnav-bc-ico"><Svg markup={meta.icon} /></span>
                <span className="tocnav-bc-lab">
                  <small>{meta.short}</small>
                  <b>{node ? node.title || "Naamloos" : `Stap ${i + 1}`}</b>
                </span>
              </div>
            </div>
          );
        })}
        {/* Small icon top-right, in the breadcrumb's right inset (absolutely
            positioned, so it takes no grid track and the steps stay aligned
            with the board columns). Hidden on an empty TOC unless full-screen
            — then the X is the way back. */}
        {(fullscreen || !modelEmpty) && (
          <button
            type="button"
            className="tocnav-fullscreen-toggle"
            aria-label={fullscreen ? "Volledig scherm sluiten" : "Op groot scherm"}
            title={fullscreen ? "Volledig scherm sluiten" : "Op groot scherm"}
            onClick={() => setFullscreen(!fullscreen)}
          >
            {fullscreen ? <CloseOutlinedIcon /> : <OpenInFullOutlinedIcon />}
          </button>
        )}
      </div>
    );
  };

  // ── detail stage ──
  const renderDetailStage = () => {
    const meta = STEP_META[STEP_ORDER[level]];
    const node = getNode(model, path[level]);
    const opts = optionsAt(level, path, model);
    const siblings = opts.filter((id) => id !== path[level]);

    return (
      <div className="tocnav-stage" style={{ "--sc": meta.color }}>
        <div className="tocnav-step-banner">
          <span className="tocnav-sb-ico"><Svg markup={meta.icon} /></span>
          <span className="tocnav-sb-meta">
            <h2>{meta.label}</h2>
            <span className="tocnav-sb-sub">{meta.sub}</span>
          </span>
          <span className="tocnav-sb-count">{opts.length} optie{opts.length === 1 ? "" : "s"}</span>
        </div>

        <div className="tocnav-active">
          <div className="tocnav-active-head">
            {/* The step banner above already names the step, so the card only
                carries an eyebrow when it adds something. While editing, the
                name and description are the first two fields — not repeated. */}
            {editingNodeId === node?.id && node ? (
              <p className="tocnav-active-editnote">Wijzigingen worden direct opgeslagen</p>
            ) : (
            <div className="tocnav-active-titles">
              {STEP_ORDER[level] === "eff" && node?.raw?.MSIId && (
                <div className="tocnav-eyebrow">Gestandaardiseerd effect (meetstandaard.nl)</div>
              )}
              {node?.step === "mech" && node.raw?.Category && (
                <div className="tocnav-eyebrow">
                  {mechanismCategoryLabels[node.raw.Category] || node.raw.Category}
                </div>
              )}
              <div className="tocnav-active-title">
                {node ? node.title || "Naamloos" : "—"}
                {node && linkedBadgeFor(node.id)}
              </div>
              {node?.raw?.Description && (
                <p className="tocnav-active-desc">{node.raw.Description}</p>
              )}
            </div>
            )}
            <div className="tocnav-active-actions">
              {canEdit && (
              <button
                className="tocnav-edit"
                onClick={() => setEditingNodeId(editingNodeId === node?.id ? null : node?.id)}
              >
                {editingNodeId === node?.id ? (
                  <>
                    <Svg markup='<path d="M3 8.5l3.2 3L13 4.5" stroke="currentColor" stroke-width="1.9" stroke-linecap="round" stroke-linejoin="round"/>' />
                    Klaar
                  </>
                ) : (
                  <>
                    <Svg markup='<path d="M4 20h4L18.5 9.5a2.1 2.1 0 0 0-3-3L5 17v3z" stroke="currentColor" stroke-width="1.7" stroke-linejoin="round"/><path d="M13.5 7.5l3 3" stroke="currentColor" stroke-width="1.7"/>' />
                    Bewerken
                  </>
                )}
              </button>
              )}
              {canEdit && node && (
                <button
                  className="tocnav-delete"
                  title={`${meta.label} verwijderen`}
                  onClick={() => setDeleteOpen(true)}
                >
                  <Svg markup='<path d="M4 7h16M9 7V5a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v2M6 7l1 13a1 1 0 0 0 1 1h8a1 1 0 0 0 1-1l1-13" stroke="currentColor" stroke-width="1.7" stroke-linecap="round" stroke-linejoin="round"/>' />
                </button>
              )}
            </div>
          </div>

          {editingNodeId === node?.id && node
            ? <NodeEditor step={STEP_ORDER[level]} item={node.raw} sectors={sectors} modules={outputModules} />
            : <NodeDetail
                node={node}
                model={model}
                outputs={outputs}
                mechanismsLoaded={Array.isArray(allMechanisms)}
                onEdit={node ? () => setEditingNodeId(node.id) : undefined}
              />}
        </div>

        <div className="tocnav-siblings">
          {siblings.length > 0 && (
            <div className="tocnav-siblings-label">Andere {meta.label.toLowerCase()} ({siblings.length})</div>
          )}
          <div className="tocnav-siblings-list">
            {siblings.map((id) => {
              const sn = getNode(model, id);
              return (
                <button className="tocnav-sib" key={id} onClick={() => pick(id, level)}>
                  <b>{sn?.title || "Naamloos"}</b>
                  {sn && linkedBadgeFor(sn.id)}
                </button>
              );
            })}
            {canEdit && (
              <button className="tocnav-sib tocnav-sib-add" onClick={() => openAdd(STEP_ORDER[level])}>
                <span className="tocnav-add-plus"><Svg markup={plusIcon} /></span>
                <b>{meta.label} toevoegen</b>
              </button>
            )}
          </div>
        </div>
      </div>
    );
  };

  // ── empty stage (no data at all): the header stays, and one add-pill sits
  // in the board grid directly under the Doelgroep step — the chain root. ──
  const renderEmptyStage = () => (
    <div className="tocnav-stage" style={{ "--sc": STEP_META.doel.color }}>
      <div className="tocnav-empty-board">
        {canEdit && (
          <button className="tocnav-pill-add" onClick={() => openAdd("doel")}>
            <span className="tocnav-add-plus"><Svg markup={plusIcon} /></span>
            <b>Doelgroep toevoegen</b>
          </button>
        )}
      </div>
    </div>
  );

  // Create/remove a chain link drawn on the Overzicht board. resolveLinkTarget
  // turns the two node IDs into the single array write (which doc/field holds the
  // link); the drag direction already guarantees a valid adjacent pair, so a null
  // target just no-ops. Errors surface (no swallow) — a failed write leaves the
  // edge unchanged rather than faking success.
  const changeLink = (op) => (fromId, toId) => {
    const target = resolveLinkTarget(model.byId.get(fromId), model.byId.get(toId));
    if (!target) return undefined;
    return db
      .collection(target.collection)
      .doc(target.docid)
      .update({ [target.field]: op(target.valueId) });
  };
  const linkNodes = changeLink((id) => firebase.firestore.FieldValue.arrayUnion(id));
  const unlinkNodes = changeLink((id) => firebase.firestore.FieldValue.arrayRemove(id));

  // Persist a column reorder. Position is the ONE ordering field: the board
  // reads it (useFirestoreOrderBy above) and so does the "Effecten uit
  // portfolio koppelen" modal on a subscribing project, so the order set here
  // is the order everyone sees. One atomic batch, so a closed tab can't leave
  // half a column renumbered. Errors surface (no swallow) — the listener simply
  // keeps the old order rather than faking a move.
  const reorderStep = (step, activeId, overId) => {
    const writes = reorderStepPositions(model.byStep[step], activeId, overId);
    if (writes.length === 0) return undefined;
    const batch = db.batch();
    writes.forEach(({ docid, Position }) => {
      batch.update(db.collection(STEP_COLLECTION[step]).doc(docid), { Position });
    });
    return batch.commit().then(() => setSaved("flex"));
  };

  // Persist one sector-context answer. Autosave per chip — never gated on the
  // block being complete, because a half-filled block is a legal state
  // (docs/pitfalls.md#ui-strict-validator-kills-autosave, shipped twice).
  //
  // buildSectorContextUpdate returns dot-paths only, and folds any answer this
  // change orphans into the SAME update — two writes would clobber siblings.
  // Errors surface (no swallow): a failed write leaves the chip as it was
  // rather than faking success.
  const answerSectorContext = (sector, questionId, value) => {
    if (!compagnyDocid) return undefined;
    return db
      .collection("CompagnyMeta")
      .doc(compagnyDocid)
      .update(
        buildSectorContextUpdate({
          sector,
          questionId,
          value,
          answers: sectorContext[sector],
          deleteSentinel: firebase.firestore.FieldValue.delete(),
        })
      )
      .then(() => setSaved("flex"));
  };

  // ── Sector context stage — same chrome as a chain step, so the questions read
  // as a place in the navigator rather than a form bolted onto the page. ──
  const renderContextStage = () => (
    <div className="tocnav-stage" style={{ "--sc": CONTEXT_META.color }}>
      {/* No icon square here, unlike a chain step's banner: it would indent the
          heading ~53px while the questions below start flush left. The pill in
          the breadcrumb already carries the icon. */}
      <div className="tocnav-step-banner sectorctx-banner">
        <span className="tocnav-sb-meta">
          <h2>{CONTEXT_META.label}</h2>
          <span className="tocnav-sb-sub">{CONTEXT_META.sub}</span>
        </span>
      </div>
      <SectorContextBand
        sectors={sectors}
        answers={sectorContext}
        canEdit={canEdit}
        onAnswer={answerSectorContext}
      />
    </div>
  );

  // ── Overzicht stage (full chain board; the breadcrumb steps above are the
  // column headers, so the board renders no header row of its own) ──
  const renderChainStage = () => (
    <div className="tocnav-stage" style={{ "--sc": OVERZICHT_META.color }}>
      <TocChainOverview
        model={model}
        stepMeta={STEP_META}
        onPick={pickFromOverview}
        onAdd={canEdit ? (step) => openAdd(step, false) : undefined}
        onLink={canEdit ? linkNodes : undefined}
        onUnlink={canEdit ? unlinkNodes : undefined}
        onReorder={canEdit ? reorderStep : undefined}
        syncedLinks={syncedLinks}
        portfolioNameById={portfolioNameById}
      />
    </div>
  );

  // The navigator card. Full-screen it is portalled to <body> so a fixed
  // overlay is never trapped by a page ancestor (transform/overflow) and sits
  // above the sidebar and topbar; the add-choosers (react-modal, z-index 5)
  // still open on top of it. Switching remounts the card — all navigator state
  // (mode, path) lives on this page, so nothing is lost.
  const navigatorCard = (
    <div className={`tocnav-root${fullscreen ? " is-fullscreen" : ""}`}>
      <div className="tocnav-card">
        {renderBreadcrumb()}
        {/* Gated on hasSectorContext as well as mode: if the sector changes
            while this stage is open (settings edited elsewhere, a colleague
            unticking it) the band would render null under a live heading,
            with the pill gone and no way back. Fall through to the board. */}
        {mode === "context" && hasSectorContext
          ? renderContextStage()
          : mode === "detail"
            ? renderDetailStage()
            : (modelEmpty ? renderEmptyStage() : renderChainStage())}
      </div>
    </div>
  );

  return (
    <div className="main">
      <div className="main-container">
        <PageHeader
          heading={"Theory of Change"}
          description={`Je Theory of Change als navigatiesysteem. Het overzicht toont alle elementen en hun koppelingen in één beeld; klik op een element om de keten stap voor stap te volgen en te bewerken.`}
          AI={true}
          deccaSubject="Theory of Change"
          deccaGreeting={deccaGreeting}
        />

        {fullscreen ? createPortal(navigatorCard, document.body) : navigatorCard}
      </div>
      <DeleteModal
        deleteModal={deleteOpen}
        setDeleteModal={setDeleteOpen}
        deleteItem={() => softDeleteNode(getNode(model, path[level]))}
        deleteName={getNode(model, path[level])?.title || "dit element"}
      />

      {/* Add-choosers per step. A flow mounts on first open (mountedFlows
          latch) and then stays mounted so a follow-up subscribe modal survives
          the chooser closing; the "eigen" option keeps the navigator's inline
          create (createNode). Both chooser paths (onOwnAdd + onAdded) honour the
          board-vs-detail link intent via addLinkRef — a board add stays
          unlinked even through the "eigen" branch. */}
      {mountedFlows.doel && (
        <AddTargetgroupFlow
          open={chooserStep === "doel"}
          setOpen={(open) => setChooserStep(open ? "doel" : null)}
          onOwnAdd={() => createNode("doel", addLinkRef.current)}
          onAdded={handleChooserAdded("doel")}
        />
      )}
      {mountedFlows.act && (
        <AddActivityFlow
          open={chooserStep === "act"}
          setOpen={(open) => setChooserStep(open ? "act" : null)}
          onOwnAdd={() => createNode("act", addLinkRef.current)}
          onAdded={handleChooserAdded("act")}
        />
      )}
      {mountedFlows.mech && (
        <AddMechanismsModal
          modalOpen={chooserStep === "mech"}
          setModalOpen={(open) => setChooserStep(open ? "mech" : null)}
          existingTitles={(model.byStep.mech || []).map((m) => m.raw?.Title || "")}
          onAdd={createMechanismNodes}
                  />
      )}
      {mountedFlows.eff && (
        <AddEffectFlow
          open={chooserStep === "eff"}
          setOpen={(open) => setChooserStep(open ? "eff" : null)}
          onOwnAdd={() => createNode("eff", addLinkRef.current)}
          onAdded={handleChooserAdded("eff")}
          effectsLength={(effects || []).length}
        />
      )}
    </div>
  );
};

export default Impactstrategy;
